// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Play and Spell Intervals
//
// First play then click letters & accidentals to create an interval.
import InteractivePrototype from './interactivePrototype.ts';
import * as Skills from '../uTheory.Skills/skills.ts';
import SkillCalculator from '../uTheory.Skills/skillCalculator.ts';
import * as Notation from '../notation.ts';
import utils from '../utils.ts';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';

// Interval name type options:
//    longNames: Perfect 4th
//    genericNames: 4th
//    halfWholeNames: Chromatic Half Step, Whole Step, minor third, etc...
//    ascendingDescending: ascending P4
//      The default is: (intervalname) + above/below.

class PlayAndSpellIntervals extends InteractivePrototype {
  constructor(displayDiv, options) {
    super();
    this.targetSpeedFromSkills = [
      'ascending_major_perfect',
      'ascending_others',
      'descending_minor_perfect',
      'descending_others',
    ];
    this.settings = {
      instructions: {
        ariaLive: 'alert',
        ariaLiveRegion: 0,
      },
      piano: {},
      letters: { letterButtons: true, accidentalButtons: true, checkButton: 'Check' },
      checkMark: {
        rightAnswerLength: 1,
        rightAnswerInOutRatio: 0.7,
        wrongAnswerLength: 1.0,
        wrongAnswerInOutRatio: 0.3,
      },
      player: {},
      oneTryOnly: false,
      progressTracker: true,
    };

    this.init(displayDiv, options);
  }

  init(displayDiv, options) {
    // copy our settings onto the prototype settings:
    utils.merge(this.settings.piano, {
      lowestNote: options.lowestNote,
      highestNote: options.highestNote,
    });

    // now call the prototype's init function:
    super.init(displayDiv, options);
  }

  generateQuestions() {
    this.questionPrompt =
      this.options.questionPrompt ||
      'Play the note {question} the given note, then label its letter name.';
    this.questions = [];

    let questions = [];

    // Either generate or calculate questions:
    if (typeof this.options.drill !== 'undefined' && this.options.drill === true) {
      questions = this.randomQuestionGenerator();
    } else {
      questions = this.prepareManualQuestions();
    }

    this.questionPrompt += `<br />Number {question} of ${questions.length}`;

    questions.forEach((question, questionNumber) => {
      const text = [this.questionText(question.interval), questionNumber + 1];
      const skills = this.calculateSkill(question.interval);

      const questionObject = { note: question.note, interval: question.interval, text, skills };

      this.questions.push(questionObject);
    }, this);

    this.note = new Notation.Note();
    this.maxQuestions = this.questions.length;
  }

  randomQuestionGenerator() {
    const generator = new QuestionGenerator();
    return generator.multipleSkillsGenerate(
      this.options.skills,
      this.options.maxQuestions,
      this.options
    );
  }

  prepareManualQuestions() {
    const intervalQuestions = [];
    this.options.questions.forEach((question) => {
      const questionParts = question.split('/');
      const note = new Notation.Note(questionParts[0]);
      const interval = new Notation.Interval(questionParts[1]);
      intervalQuestions.push({ note, interval });
    }, this);
    return intervalQuestions;
  }

  calculateSkill(interval) {
    if (typeof this.options.forceSkill !== 'undefined') {
      return [Skills.makeInt(this.options.forceSkill)];
    }
    // otherwise, invoke a skillCalculator:
    if (typeof this.skillCalculator === 'undefined') {
      this.skillCalculator = new SkillCalculator();
    }

    if (
      typeof this.options.halfWholeNames !== 'undefined' &&
      this.options.halfWholeNames === true
    ) {
      return [
        this.skillCalculator.getPianoHalfWholeSkill(interval),
        this.skillCalculator.getStaffHalfWholeSkill(interval),
      ];
    }
    // GCR: Expand logic to other intervals later
    return false;
  }

  questionText(interval) {
    const intervalText = this.intervalText(interval);
    const directionText = this.directionText(interval);
    const ascDsc =
      typeof this.options.ascendingDescending !== 'undefined' &&
      this.options.ascendingDescending === true;

    if (typeof this.options.directionOnly !== 'undefined' && this.options.directionOnly === true) {
      return directionText;
    }

    if (ascDsc) {
      return `${utils.aOrAn(directionText)} ${directionText} ${intervalText}`;
    }
    return `${utils.aOrAn(intervalText)} ${intervalText} <strong>${directionText}</strong>`;
  }

  intervalText(interval) {
    // longNames: Perfect 4th, minor 2nd, Augmented octave
    if (typeof this.options.longNames !== 'undefined' && this.options.longNames === true) {
      return interval.longName();
    }
    if (
      typeof this.options.halfWholeNames !== 'undefined' &&
      this.options.halfWholeNames === true
    ) {
      return interval.longHalfWholeName();
    }
    if (typeof this.options.genericNames !== 'undefined' && this.options.genericNames === true) {
      return interval.longGenericName();
    }
    return interval.quality + interval.genericInterval;
  }

  directionText(interval) {
    if (
      typeof this.options.ascendingDescending !== 'undefined' &&
      this.options.ascendingDescending === true
    ) {
      return interval.direction > 0 ? 'ascending' : 'descending';
    }
    return interval.direction > 0 ? 'above' : 'below';
  }

  newQuestion() {
    this.letterPanel.clearNoteName();
    this.piano.clearColors();
    this.piano.clearNoteLabels();
    this.note = this.questions[this.questionNumber].note;
    this.interval = this.questions[this.questionNumber].interval;
    const newMidi = this.note.getMidiNumber();
    this.piano.colorNote(newMidi);
    this.piano.labelNote(newMidi, this.note.letter + this.note.accidental);
    super.newQuestion();
    this.answerMidi = undefined;
    this.answerLetter = undefined;
    this.answerAccidental = '';
    this.wrongAnswerCount = 0;
    this.priorMidi = undefined;
  }

  addListeners() {
    this.piano.addNoteOnListener(this.keyPressed, this);
    this.letterPanel.addLetterCallback(this.letter, this);
    this.letterPanel.addAccidentalCallback(this.accidental, this);
    this.letterPanel.addCheckCallback(this.check, this);
  }

  letter(letter) {
    this.clearWarning();
    if (typeof this.answerMidi === 'undefined' && this.questionNumber === 0) {
      this.checkMark.warn('Play the answer on the piano first.');
    }
    this.answerLetter = letter;
    this.updateDisplay();
  }

  accidental(accidental) {
    this.clearWarning();
    if (typeof this.answerMidi === 'undefined' && this.questionNumber === 0) {
      this.checkMark.warn('Play the answer on the piano first.');
    }
    this.answerAccidental = accidental;
    this.updateDisplay();
  }

  keyPressed(midiNumber) {
    this.clearWarning();
    if (midiNumber !== this.note.getMidiNumber()) {
      this.priorMidi = this.answerMidi;
      this.answerMidi = midiNumber;
      this.updateDisplay();
    }
  }

  updateDisplay() {
    if (typeof this.priorMidi !== 'undefined') {
      this.piano.clearNoteColoring(this.priorMidi);
      this.piano.clearNoteLabel(this.priorMidi);
      this.priorMidi = this.answerMidi;
    }
    let answerString = typeof this.answerLetter !== 'undefined' ? this.answerLetter : '';
    answerString += typeof this.answerAccidental !== 'undefined' ? this.answerAccidental : '';
    if (typeof this.answerLetter !== 'undefined') {
      if (
        this.answerAccidental === '' &&
        this.answerLetter === this.note.letter &&
        this.note.accidental !== ''
      ) {
        answerString += 'n';
      }
    }

    if (typeof this.answerMidi !== 'undefined') {
      this.piano.labelNote(this.answerMidi, answerString);
      this.piano.colorNote(this.answerMidi);
    }
  }

  clearWarning() {
    this.checkMark.off();
  }

  check() {
    if (this.rightAnswerDelay) return;
    // If they haven't completed all the steps of the question,
    // give them a polite warning instead of marking them wrong:
    if (typeof this.answerMidi === 'undefined') {
      this.checkMark.warn('Play the answer on the piano first.');
      return;
    }
    if (typeof this.answerLetter === 'undefined' || this.answerLetter === '') {
      this.checkMark.warn('Click a letter & accidental to name the answer first.');
      return;
    }

    const goalNote = this.note.getNoteAt(this.interval);
    const goalMIDI = goalNote.getMidiNumber();
    this.goalMIDI = goalMIDI;

    if (this.interval.direction === 1 && this.answerMidi < this.note.getMidiNumber()) {
      this.checkMark.warn('Careful: write an ascending, not descending interval.');
      return;
    }
    if (this.interval.direction === -1 && this.answerMidi > this.note.getMidiNumber()) {
      this.checkMark.warn('Careful: write a descending, not ascending interval.');
      return;
    }

    const answerNote = new Notation.Note(this.answerLetter + this.answerAccidental);

    const rightLetter = goalNote.equalsIgnoreOctave(answerNote);
    // eslint-disable-next-line eqeqeq
    const rightMidi = goalMIDI == this.answerMidi;
    if (rightLetter && rightMidi) {
      this.rightAnswer(1000);
    } else if (this.wrongAnswerCount > 0) {
      // Wrong Answer:
      // on the second wrong answer in a row, give a hint:
      let wrongAnswerText = '';
      if (!rightMidi) wrongAnswerText = 'Wrong piano note.';
      if (!rightMidi && !rightLetter) wrongAnswerText += '<br />';
      if (!rightLetter) {
        wrongAnswerText += 'Wrong ';
        let spacer = '';
        if (goalNote.letter !== answerNote.letter) {
          wrongAnswerText += 'letter';
          spacer = ' & ';
        }
        if (goalNote.accidental !== answerNote.accidental) {
          wrongAnswerText += `${spacer}accidental`;
        }
        wrongAnswerText += '.';
      }
      this.wrongAnswer(wrongAnswerText);
    } else {
      this.wrongAnswer();
      this.wrongAnswerCount++;
    }
  }

  debugRightAnswer() {
    const goalNote = this.note.getNoteAt(this.interval);
    const goalMIDI = goalNote.getMidiNumber();
    this.goalMIDI = goalMIDI;
    this.rightAnswer();
  }

  gameOver() {
    this.instructionsDiv.innerHTML = 'Nice work!';
    this.instructionsDiv.setAttribute('role', 'alert');
    this.piano.removeListeners();
    this.displayDiv.removeChild(this.lettersDiv);
    super.gameOver();
  }

  playAnswer() {
    this.player.play(this.note.getMidiNumber(), { startTime: 0, stopTime: 0.6 });
    this.player.play(this.goalMIDI, { startTime: 0.5, stopTime: 1.25 });
  }
}

export default PlayAndSpellIntervals;
